import { formatCurrency } from '@/utils/money';
import { NetworkStatus } from '@apollo/client';
import { partnerSupplier } from '@/utils/environment';
import {
  selectSalesPortalCartOrderId,
  useCartStore,
} from '@/experiences/sales-portal/domain/ordering/blocs/stores/cart-store';
import { useCallback, useMemo, useRef } from 'react';
import { useGetOrderEntryEstimatedCommissionQuery } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/documents/get-order-entry-estimated-commission.generated';

interface UseOrderEntryCommissionLoaderParams {
  authenticatedUserUUID: string | null;
  currencyCode: string | null;
  enabled: boolean;
  orderUUID: string | null;
}

interface OrderEntryCommissionLoaderResult {
  commissionDisplayValue: string | null;
  isCommissionsLoading: boolean;
  refetchCommission: (() => void) | null;
}

/**
 * Loader that fetches the estimated commission for a given order and
 * returns a formatted display value. Uses network-only fetch policy
 * since commission is always refetched explicitly after cart/price changes.
 *
 * @param params.authenticatedUserUUID - The authenticated user's UUID
 * @param params.currencyCode - The currency code for formatting
 * @param params.enabled - Whether the commissions experiment is enabled
 * @param params.orderUUID - The order UUID from the cart store
 * @returns The formatted commission display value, loading state, and refetch function
 */
export const useOrderEntryCommissionLoader = ({
  authenticatedUserUUID,
  currencyCode,
  enabled,
  orderUUID,
}: UseOrderEntryCommissionLoaderParams): OrderEntryCommissionLoaderResult => {
  const shouldSkip = !enabled || !orderUUID || !authenticatedUserUUID;

  // Use ref to track skip state so safeRefetch always checks current value,
  // even when called from stale closures (e.g., after cart is emptied)
  const shouldSkipRef = useRef(shouldSkip);
  shouldSkipRef.current = shouldSkip;

  const { data, networkStatus, refetch } =
    useGetOrderEntryEstimatedCommissionQuery({
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      skip: shouldSkip,
      variables: {
        businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
        employeeUUID: authenticatedUserUUID!,
        orderUUID: orderUUID!,
      },
    });

  const loading =
    networkStatus === NetworkStatus.loading ||
    networkStatus === NetworkStatus.refetch;

  // Guard refetch to prevent calling with invalid/empty variables (e.g., empty cart).
  // Uses ref to check current skip state at call time, not at callback creation time.
  const safeRefetch = useCallback(() => {
    if (shouldSkipRef.current) {
      return Promise.resolve();
    }
    // There is a possible race condition between the render where
    // shouldSkipRef is set and safeRefetch is called for the order to be canceled
    const currentOrderUUID = selectSalesPortalCartOrderId(
      useCartStore.getState(),
    );
    if (!currentOrderUUID) {
      return Promise.resolve();
    }
    return refetch({
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      employeeUUID: authenticatedUserUUID!,
      orderUUID: currentOrderUUID,
    });
  }, [authenticatedUserUUID, refetch]);

  const commissionDisplayValue = useMemo(() => {
    if (!enabled) {
      return null;
    }
    const commission = data?.getOrderEstimatedCommission?.commissions?.[0];
    const estimatedCommissionInMicros =
      commission?.estimated_commission_in_micros ?? 0;
    return formatCurrency(estimatedCommissionInMicros, currencyCode);
  }, [currencyCode, data, enabled]);

  if (!enabled) {
    return {
      commissionDisplayValue: null,
      isCommissionsLoading: false,
      refetchCommission: null,
    };
  }

  return {
    commissionDisplayValue,
    isCommissionsLoading: loading,
    refetchCommission: safeRefetch,
  };
};
